import { useState } from "react";
import {
  ALL_ACCOUNTS,
  accountEnvTag,
  rithmicLoginLine,
  useTrading,
  type AccountEnvKind,
  type AccountEnvTag,
} from "@helm/core";
import { ALL_ACCOUNTS_NOTE, TRADING_ACCOUNT, TRADING_ACCOUNT_NOTE } from "@helm/ui";
import { settingsSync } from "../session";
import { setAccountSelection, type AccountView } from "../viewmodel/account";
import { usePopover } from "../components/Popover";
import { Icon } from "../components/Icon";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { resyncWithRithmic } from "../trading/actions";
import "./rithmicAccount.css";

/** Most real first: an "All accounts" view that includes a live account is tagged live. */
const ENV_RANK: Record<AccountEnvKind, number> = { live: 3, demo: 2, simulation: 1 };

function selectedEnv(view: AccountView): AccountEnvKind | null {
  if (!view.allSelected) return view.active?.environment ?? null;
  let best: AccountEnvKind | null = null;
  for (const a of view.accounts)
    if (best === null || ENV_RANK[a.environment] > ENV_RANK[best]) best = a.environment;
  return best;
}

/** The tag the control shows: the selected account's, or "ALL" in the most real tone present. */
function selectedEnvTag(view: AccountView): AccountEnvTag {
  const t = accountEnvTag(selectedEnv(view));
  if (!view.allSelected || view.accounts.length === 0) return t;
  return {
    tag: "ALL",
    tone: t.tone,
    title: `All accounts — includes ${t.tag === "LIVE" ? "a LIVE (real money) account" : `${t.tag} accounts`}`,
  };
}

/** A LIVE / PROP / SIM tag (also used by the copy-trading setup). */
export function EnvTag({ t }: { t: AccountEnvTag }): React.JSX.Element {
  return (
    <span className={`ra-env-tag ra-tone-${t.tone}`} title={t.title} data-tone={t.tone}>
      {t.tag}
    </span>
  );
}

/** The login line for the control (Rithmic system + connection state). */
function useLoginLine(): ReturnType<typeof rithmicLoginLine> {
  const login = useTrading((t) => t.login);
  const session = useTrading((t) => t.session);
  return rithmicLoginLine({ login, session });
}

const GROUPS = [
  ["live", "Live — real money"],
  ["demo", "Prop / paper"],
  ["simulation", "Simulation / test"],
] as const;

/**
 * The Rithmic account control at the top left (owner 2026-09-30, after Tradesea): which Rithmic
 * system the session trades through and whether it is connected, the account shown and traded
 * (with its own LIVE / PROP / SIM tag), and a menu to switch accounts or manage the Rithmic
 * logins: on desktop "Add Rithmic account…" and "Log in to Rithmic…" open the Rithmic login
 * dialog (owner 2026-10-04); on mobile "Rithmic logins…" opens the Account tab. The star marks
 * the main account (synced setting). Positions / Orders / Fills follow the selection unless their
 * own filter says otherwise.
 */
export function RithmicAccountControl({
  view,
  privacy,
  onOpenRithmic,
  onAddRithmic,
  onLogInRithmic,
  compact = false,
  showLogin = true,
}: {
  view: AccountView;
  privacy: boolean;
  /** Opens the Rithmic logins (the Account tab on mobile). */
  onOpenRithmic?: () => void;
  /** "Add Rithmic account…": the Rithmic login dialog with the Connect form open (desktop). */
  onAddRithmic?: () => void;
  /** "Log in to Rithmic…": the Rithmic login dialog with the stored logins (desktop). */
  onLogInRithmic?: () => void;
  compact?: boolean;
  /**
   * Show the Rithmic login line ("Rithmic · Apex · Connected") in the control and its menu. The
   * desktop status line shows the connection once, in its own item (ConnectionItem), so it passes
   * false; mobile keeps the line here. Without the line the control reads "Trading account" and
   * its menu says it is the trading account for every panel (AccountSelect writes the same one
   * selection, so picking an account in any panel changes it here too).
   */
  showLogin?: boolean;
}): React.JSX.Element {
  const pop = usePopover();
  const line = useLoginLine();
  const heldExits = useTrading((t) => t.heldExits.size);
  const [resyncAsk, setResyncAsk] = useState(false);
  const [resyncBusy, setResyncBusy] = useState(false);
  // ADR-0059: rebuild the login's orders and positions from Rithmic, without a restart.
  const resyncWhyNot =
    line.state !== "connected"
      ? "Connect to Rithmic first: a connect re-reads everything by itself"
      : heldExits > 0
        ? "An exit is held (the red banner): finish it or Force exit first"
        : null;
  const env = selectedEnvTag(view);
  const label = view.allSelected
    ? `All accounts (${String(view.accounts.length)})`
    : view.active
      ? privacy
        ? "***"
        : view.active.label
      : "No account";
  const isMain = !view.allSelected && view.active !== null && view.active.id === view.mainId;
  // "Rithmic · Apex · Connected"; a system already named Rithmic… is not repeated.
  const rithmicText = /^rithmic\b/i.test(line.system)
    ? `${line.system} · ${line.stateText}`
    : `Rithmic · ${line.system} · ${line.stateText}`;
  return (
    <div
      className={compact ? "popover-anchor rithmic-account ra-compact" : "popover-anchor rithmic-account"}
      data-env={env.tone}
    >
      <button
        type="button"
        className="account-btn rithmic-account-btn"
        aria-haspopup="dialog"
        title={`${env.title}. ${
          showLogin
            ? `${rithmicText}. ${
                view.allSelected
                  ? "Showing every account: figures are totals; pick one account to trade from the DOM and order pad."
                  : "The account shown in the top bar and traded from the DOM and order pad."
              }`
            : view.allSelected
              ? `${ALL_ACCOUNTS_NOTE} ${TRADING_ACCOUNT_NOTE}`
              : `${TRADING_ACCOUNT_NOTE} The DOM, order pad and chart trading send orders to it.`
        }`}
        {...pop.triggerProps}
      >
        {showLogin && (
          <span className="ra-login" data-state={line.state}>
            <span className="ra-login-dot" aria-hidden="true" />
            <span className="ra-login-text">{rithmicText}</span>
          </span>
        )}
        <span className="ra-account">
          {!showLogin && <span className="ra-caption">{TRADING_ACCOUNT}</span>}
          <EnvTag t={env} />
          <span className="account-label">{label}</span>
          {isMain && <span className="main-tag">MAIN</span>}
          <Icon name="chevronDown" size={12} />
        </span>
      </button>
      {pop.open && (
        <div className="popover account-menu" role="dialog" aria-label="Rithmic accounts" {...pop.panelProps}>
          {!showLogin && (
            <p className="ra-menu-note" data-testid="trading-account-note">
              {TRADING_ACCOUNT_NOTE} {ALL_ACCOUNTS_NOTE}
            </p>
          )}
          {showLogin && (
            <div className="ra-menu-login" data-state={line.state}>
              <span className="ra-login-dot" aria-hidden="true" />
              <span className="ra-menu-login-text">
                <span className="account-group-title">Rithmic connection</span>
                <span>
                  {line.system} · {line.stateText}
                </span>
              </span>
              {onOpenRithmic && (
                <button
                  type="button"
                  className="btn btn-sm"
                  title="Connect, switch or disconnect your Rithmic logins; Allow trading per login"
                  onClick={() => {
                    pop.setOpen(false);
                    onOpenRithmic();
                  }}
                >
                  Rithmic logins…
                </button>
              )}
            </div>
          )}
          <div className="ra-menu-actions" role="group" aria-label="Rithmic logins">
            {onLogInRithmic && (
              <button
                type="button"
                className="account-item"
                title="Connect or disconnect a stored Rithmic login; test it; Allow trading per login"
                onClick={() => {
                  pop.setOpen(false);
                  onLogInRithmic();
                }}
              >
                <Icon name="user" size={14} />
                <span className="account-item-label">Log in to Rithmic…</span>
              </button>
            )}
            {onAddRithmic && (
              <button
                type="button"
                className="account-item"
                title="Store a new Rithmic login (server, system, user name, password) and connect it"
                onClick={() => {
                  pop.setOpen(false);
                  onAddRithmic();
                }}
              >
                <Icon name="plus" size={14} />
                <span className="account-item-label">Add Rithmic account…</span>
              </button>
            )}
            <button
              type="button"
              className="account-item"
              data-testid="resync-rithmic"
              disabled={resyncWhyNot !== null}
              title={
                resyncWhyNot ??
                "Re-read working orders, order history and positions from Rithmic. Only broker-confirmed updates are applied; omitted orders remain possibly live. Sends and cancels nothing."
              }
              onClick={() => {
                pop.setOpen(false);
                setResyncAsk(true);
              }}
            >
              <Icon name="replay" size={14} />
              <span className="account-item-label">Resync with Rithmic…</span>
              {resyncWhyNot !== null && <span className="muted">{resyncWhyNot}</span>}
            </button>
          </div>
          <button
            type="button"
            className={view.allSelected ? "account-item active" : "account-item"}
            aria-pressed={view.allSelected}
            disabled={view.accounts.length === 0}
            title="Show totals over every account; Positions / Orders / Fills that follow the top bar show every account"
            onClick={() => {
              setAccountSelection(ALL_ACCOUNTS);
              pop.setOpen(false);
            }}
          >
            <span className="status-dot status-all" aria-hidden="true" />
            <span className="account-item-label">All accounts</span>
            <span className="muted">{view.accounts.length}</span>
          </button>
          {GROUPS.map(([kind, g]) => {
            const list = view.accounts.filter((a) => a.environment === kind);
            const tag = accountEnvTag(kind);
            return (
              <div key={kind} className="account-group" data-env={tag.tone}>
                <div className="account-group-title">{g}</div>
                {list.length === 0 ? (
                  <div className="muted account-empty">No {g.split(" ")[0]?.toLowerCase()} accounts</div>
                ) : (
                  list.map((a) => {
                    const selected = !view.allSelected && a.id === view.active?.id;
                    const main = a.id === view.mainId;
                    return (
                      <div key={a.id} className="account-row">
                        <button
                          type="button"
                          className={selected ? "account-item active" : "account-item"}
                          aria-pressed={selected}
                          title={`${tag.title}. Show and trade this account.`}
                          onClick={() => {
                            setAccountSelection(a.id);
                            pop.setOpen(false);
                          }}
                        >
                          <span className={`status-dot status-${a.status}`} aria-hidden="true" />
                          <EnvTag t={tag} />
                          <span className="account-item-label">{privacy ? "***" : a.label}</span>
                          {main && <span className="main-tag">MAIN</span>}
                          <span className="muted">
                            {a.status === "locked" ? "TradePrizm lock active" : "Broker status unavailable"}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={
                            main ? "icon-btn icon-btn-xs star-btn on" : "icon-btn icon-btn-xs star-btn"
                          }
                          aria-pressed={main}
                          aria-label={
                            main ? `${a.label} is the main account` : `Make ${a.label} the main account`
                          }
                          title={
                            main
                              ? "Main account (used by “Main account only” views and by the DOM while All accounts is selected)"
                              : "Make this the main account"
                          }
                          onClick={() => {
                            if (!main) void settingsSync.update({ main_account_id: a.id });
                          }}
                        >
                          <Icon name={main ? "starFilled" : "star"} size={12} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}
          <label className="check-row muted">
            <input type="checkbox" disabled />
            <span>Hide Inactive</span>
          </label>
        </div>
      )}
      {resyncAsk && (
        <ConfirmDialog
          title="Resync with Rithmic?"
          confirmLabel="Resync"
          busy={resyncBusy}
          className="resync-confirm"
          onCancel={() => {
            setResyncAsk(false);
          }}
          onConfirm={() => {
            setResyncBusy(true);
            void resyncWithRithmic().finally(() => {
              setResyncBusy(false);
              setResyncAsk(false);
            });
          }}
        >
          <p>
            TradePrizm re-reads this login&apos;s working orders, today&apos;s order history and positions
            from Rithmic. Only broker-confirmed updates are applied. An order missing from a response remains
            possibly live, including one stuck on “Cancelling…” or “Modifying…”.
          </p>
          <p>
            <strong>It does not cancel, modify or send any order.</strong> Positions show as unconfirmed for a
            moment while Rithmic answers. Refused while a managed bracket or copy group is active (including
            paused copy groups), automated liquidation rules are armed, an exit is held or an order is still
            waiting for Rithmic&apos;s first answer. Existing protective orders keep working.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
